import { useState } from 'react'
import { useRouter } from 'next/router'
import { useTheme } from '@mui/material/styles'
import Link from 'next/link'
import { Box, Button, Typography, Grid, Checkbox, Stack } from '@mui/material'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, useForm } from 'react-hook-form'
import { useSnackbar } from 'notistack'
import {
  CalendarToday,
  Edit,
  ExitToApp,
  LockOpen,
  Settings
} from '@mui/icons-material'

import routes from '@config/routes'
import { postSignUp } from '@api/auth'
import { useTranslation } from '@contexts/translation'
import useButtonTabsStyle from '@styles/buttonTabs/buttonTabs'
// import { GoogleAnalytics } from '@utils/googleAnalytics'
import TextFieldItem from '@components/common/Form/TextFieldItem'
import clsx from 'clsx'
import { responseHandler } from '@utils/responseHandler'
import LoaderSm from '@components/common/Loader/LoaderSm'

const BACKGROUND_IMAGE = '/background.png'
const TERMS_OF_SERVICE_URL = 'https://ooti.co/en/conditions-generales/'
const PRIVACY_POLICY_URL = 'https://ooti.co/en/politiquedeconfidentialite/'

const Signup = () => {
  const router = useRouter()
  const { dictionary, lang } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()
  const classesButtonTabs = useButtonTabsStyle()
  const theme = useTheme()

  const [isLoading, setIsLoading] = useState(false)
  const [policiesAccepted, setPoliciesAccepted] = useState(false)

  const schema = yup.object().shape({
    first_name: yup.string().required(dictionary.required),
    last_name: yup.string().required(dictionary.required),
    username: yup.string().email().required(dictionary.required),
    password: yup
      .string()
      .min(8, dictionary.errors_password_minLength)
      .test('isValid', dictionary.errors_password_pattern, value => {
        const hasAlpha = /[aA-zZ]/.test(value)
        const hasNum = /[0-9]/.test(value)
        if (hasAlpha && hasNum) return true
        return false
      })
      .required(dictionary.required),
    password_again: yup
      .string()
      .oneOf([yup.ref('password'), null], dictionary.errors_password_not_match)
      .required(dictionary.required)
  })

  const {
    handleSubmit,
    control,
    formState: { errors },
    setError
  } = useForm({
    resolver: yupResolver(schema)
  })

  const onSubmit = async values => {
    const payload = {
      first_name: values.first_name,
      last_name: values.last_name,
      email: values.username,
      username: values.username,
      password: values.password,
      locale: lang
    }

    setIsLoading(true)
    const response = await postSignUp(payload)

    responseHandler({
      response,
      callback: async () => {
        router.push({
          pathname: routes.account.registration,
          query: { email: values.username }
        })
      },
      dictionary,
      snackbar: enqueueSnackbar,
      setError
    })
    setIsLoading(response.data.errors ? false : true)
  }

  return (
    <>
      <Box
        sx={{
          backgroundImage: `url(${BACKGROUND_IMAGE})`,
          backgroundPosition: 'top',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          display: 'flex',
          alignItems: 'top',
          justifyContent: 'center',
          minHeight: '100vh'
        }}
      >
        <Box
          sx={{
            width: t => t.spacing(49),
            bgcolor: t => t.palette.elements.white,
            borderRadius: t => t.spacing(1),
            padding: t => t.spacing(3),
            height: 'fit-content',
            mt: t => t.spacing(15),
            mb: t => t.spacing(10)
          }}
        >
          {isLoading ? (
            <LoaderSm />
          ) : (
            <form data-cy={'form'} noValidate onSubmit={handleSubmit(onSubmit)}>
              <Stack spacing={3}>
                <Box sx={{ margin: 'auto' }}>
                  <Box
                    component="img"
                    src={'/ooti_logo.png'}
                    sx={{
                      objectFit: 'cover',
                      width: t => t.spacing(20),
                      py: t => t.spacing(3)
                    }}
                  />
                </Box>
                <Grid
                  item
                  xs={12}
                  display="flex"
                  justifyContent="space-between"
                >
                  <Link href={routes.account.login} passHref>
                    <Button
                      startIcon={<LockOpen />}
                      className={clsx(classesButtonTabs.root, {
                        [classesButtonTabs.selected]: false
                      })}
                    >
                      {dictionary.sign_in}
                    </Button>
                  </Link>
                  <Button
                    startIcon={<Edit />}
                    className={clsx(classesButtonTabs.root, {
                      [classesButtonTabs.selected]: true
                    })}
                  >
                    {dictionary.sign_up}
                  </Button>
                </Grid>

                <Stack direction="row" spacing={3}>
                  <Grid item xs={6}>
                    <Controller
                      name="first_name"
                      control={control}
                      defaultValue={''}
                      render={props => (
                        <TextFieldItem
                          required
                          placeholder={dictionary.first_name}
                          label={dictionary.first_name}
                          errorMessage={errors.first_name?.message}
                          {...props}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Controller
                      name="last_name"
                      control={control}
                      defaultValue={''}
                      render={props => (
                        <TextFieldItem
                          required
                          placeholder={dictionary.last_name}
                          label={dictionary.last_name}
                          errorMessage={errors.last_name?.message}
                          {...props}
                        />
                      )}
                    />
                  </Grid>
                </Stack>

                <Grid item xs={12}>
                  {/* Email */}
                  <Controller
                    name="username"
                    control={control}
                    defaultValue={''}
                    render={props => (
                      <TextFieldItem
                        required
                        placeholder={dictionary.email_address}
                        label={dictionary.email_address}
                        errorMessage={errors.username?.message}
                        {...props}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  {/* Password */}
                  <Controller
                    name="password"
                    control={control}
                    defaultValue={''}
                    render={props => (
                      <TextFieldItem
                        autoComplete="on"
                        type="password"
                        required
                        placeholder={dictionary.password}
                        label={dictionary.password}
                        errorMessage={errors.password?.message}
                        {...props}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  {/* Password Again */}
                  <Controller
                    name="password_again"
                    control={control}
                    defaultValue={''}
                    render={props => (
                      <TextFieldItem
                        type="password"
                        required
                        placeholder={dictionary.new_password_again}
                        label={dictionary.new_password_again}
                        errorMessage={errors.password_again?.message}
                        {...props}
                      />
                    )}
                  />
                </Grid>

                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={3}
                >
                  <Box
                    sx={{
                      borderRadius: '4px',
                      bgcolor: t => t.palette.secondary.xtraLight,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: t => t.spacing(12),
                      height: t => t.spacing(12)
                    }}
                  >
                    <Grid sx={{ pt: 1.5 }} align="center">
                      <CalendarToday color="secondary" />
                    </Grid>
                    <Stack
                      justifyContent="center"
                      alignItems="center"
                      sx={{ height: '100%', textAlign: 'center' }}
                    >
                      <Typography variant="subtitle2" color="secondary">
                        {dictionary.free_days_trial}
                      </Typography>
                    </Stack>
                  </Box>
                  <Box
                    sx={{
                      borderRadius: '4px',
                      bgcolor: t => t.palette.secondary.xtraLight,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: t => t.spacing(12),
                      height: t => t.spacing(12)
                    }}
                  >
                    <Grid sx={{ pt: 1.5 }} align="center">
                      <Settings color="secondary" />
                    </Grid>
                    <Stack
                      justifyContent="center"
                      alignItems="center"
                      sx={{ height: '100%', textAlign: 'center' }}
                    >
                      <Typography variant="subtitle2" color="secondary">
                        {dictionary.easy_setup}
                      </Typography>
                    </Stack>
                  </Box>
                  <Box
                    sx={{
                      borderRadius: '4px',
                      bgcolor: t => t.palette.secondary.xtraLight,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: t => t.spacing(12),
                      height: t => t.spacing(12)
                    }}
                  >
                    <Grid sx={{ pt: 1.5 }} align="center">
                      <ExitToApp color="secondary" />
                    </Grid>
                    <Stack
                      justifyContent="center"
                      alignItems="center"
                      sx={{ height: '100%', textAlign: 'center' }}
                    >
                      <Typography variant="subtitle2" color="secondary">
                        {dictionary.cancel_anytime}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ mb: 6 }}
                >
                  <Checkbox
                    checked={policiesAccepted}
                    onChange={() => setPoliciesAccepted(prev => !prev)}
                  />
                  <Stack>
                    <Typography variant="body2">
                      {`${dictionary.read_accepted} `}
                      <span
                        style={{
                          color: theme.palette.primary.main,
                          textDecoration: 'underline'
                        }}
                      >
                        <Link href={TERMS_OF_SERVICE_URL} passHref>
                          {dictionary.the_terms_of_service}
                        </Link>
                      </span>
                    </Typography>
                    <Typography variant="body2">
                      {` ${dictionary.and} `}
                      <span
                        style={{
                          color: theme.palette.primary.main,
                          textDecoration: 'underline'
                        }}
                      >
                        <Link href={PRIVACY_POLICY_URL} passHref>
                          {dictionary.privacy_policy}
                        </Link>
                      </span>
                    </Typography>
                  </Stack>
                </Stack>

                <Grid item xs={12} align="center">
                  <Button type="submit" disabled={!policiesAccepted}>
                    {dictionary.sign_up}
                  </Button>
                </Grid>
              </Stack>
            </form>
          )}
        </Box>
      </Box>
    </>
  )
}

export default Signup
